import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  Heading,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Skeleton,
  Stack,
  Text,
  Tooltip,
  useClipboard,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import {
  MdCall,
  MdContentCopy,
  MdEmail,
  MdOpenInNew,
  MdPayment,
  MdRefresh,
  MdReplay,
  MdSms,
  MdVisibility,
  MdVisibilityOff,
} from "react-icons/md";
import { AiOutlineDeploymentUnit } from "react-icons/ai";
import Card from "components/card/Card.js";
import { aloApi, describeError } from "../api";
import { useWithFreshToken } from "../hooks";
import { dubaiTime, displayPhone, groupCode, humanize, maskCode, money, statusLabel, telHref } from "../format";
import StatusBadge from "./StatusBadge";
import ConfirmDialog from "./ConfirmDialog";

const RETRY_RECHECK_MS = 2 * 60 * 1000 + 15 * 1000;

function Field({ label, children }) {
  return (
    <Box>
      <Text fontSize="xs" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="secondaryGray.600">
        {label}
      </Text>
      <Box mt={1} fontSize="sm" fontWeight="600" wordBreak="break-all">
        {children}
      </Box>
    </Box>
  );
}

function CopyValue({ value, children }) {
  const { hasCopied, onCopy } = useClipboard(value || "");
  if (!value) return <Text as="span">—</Text>;
  return (
    <Flex align="center" gap={1}>
      <Text as="span">{children || value}</Text>
      <Tooltip label={hasCopied ? "Copied" : "Copy"}>
        <IconButton size="xs" variant="ghost" aria-label="Copy" icon={<MdContentCopy />} onClick={onCopy} />
      </Tooltip>
    </Flex>
  );
}

function VoucherCode({ voucher }) {
  const [shown, setShown] = useState(false);
  const { hasCopied, onCopy } = useClipboard(voucher ? voucher.code : "");
  const codeBg = useColorModeValue("orange.50", "whiteAlpha.100");
  useEffect(() => setShown(false), [voucher && voucher.code]);

  if (!voucher || !voucher.code) {
    return (
      <Text fontSize="sm" color="secondaryGray.600">
        No code issued yet{voucher && voucher.status ? ` (Giftery: ${humanize(voucher.status)})` : ""}.
      </Text>
    );
  }
  return (
    <Box>
      <Flex align="center" gap={2} bg={codeBg} border="2px dashed" borderColor="orange.300" borderRadius="12px" px={4} py={3}>
        <Text fontFamily="mono" fontSize="xl" fontWeight="700" letterSpacing="0.08em" flex="1" whiteSpace="nowrap">
          {shown ? groupCode(voucher.code) : maskCode(voucher.code)}
        </Text>
        <Tooltip label={shown ? "Hide" : "Show"}>
          <IconButton
            size="sm"
            variant="ghost"
            aria-label={shown ? "Hide code" : "Show code"}
            icon={shown ? <MdVisibilityOff /> : <MdVisibility />}
            onClick={() => setShown((value) => !value)}
          />
        </Tooltip>
        <Button size="sm" leftIcon={<MdContentCopy />} onClick={onCopy} colorScheme="orange" variant="outline">
          {hasCopied ? "Copied" : "Copy"}
        </Button>
      </Flex>
      <Grid templateColumns="repeat(3, 1fr)" gap={4} mt={3}>
        <Field label="Serial">{voucher.serial || "—"}</Field>
        <Field label="Use before">{dubaiTime(voucher.expiresAt, "DD MMM YYYY")}</Field>
        <Field label="Giftery">{humanize(voucher.status)}</Field>
      </Grid>
    </Box>
  );
}

function Timeline({ order }) {
  const lineColor = useColorModeValue("gray.200", "whiteAlpha.300");
  const events = useMemo(() => {
    const statusEvents = (order.statusLogs || []).map((log) => ({
      at: log.at,
      kind: "status",
      title: statusLabel(log.status),
      detail: log.comment,
    }));
    const paymentEvents = (order.paymentLogs || []).map((log) => ({
      at: log.at,
      kind: "payment",
      title: `Payment ${humanize(log.status).toLowerCase()}`,
      detail: [
        log.paymentOrderId ? `PayBy order ${log.paymentOrderId}` : null,
        log.paidAmount !== null && log.paidAmount !== undefined ? money(log.paidAmount, order.currency) : null,
      ]
        .filter(Boolean)
        .join(" · "),
    }));
    return [...statusEvents, ...paymentEvents].sort((a, b) => new Date(b.at) - new Date(a.at));
  }, [order]);

  if (events.length === 0) {
    return (
      <Text fontSize="sm" color="secondaryGray.600">
        No history yet.
      </Text>
    );
  }
  // Newest first: a vertical line with one dot per event.
  return (
    <Box position="relative" ps="40px" pt={2}>
      <Box position="absolute" left="13px" top="12px" bottom="12px" w="2px" bg={lineColor} />
      <Stack spacing={4}>
        {events.map((event, i) => (
          <Box key={`${event.kind}-${event.at}-${i}`} position="relative">
            <Flex
              position="absolute"
              left="-40px"
              top="2px"
              w="28px"
              h="28px"
              borderRadius="50%"
              align="center"
              justify="center"
              color="white"
              bg={event.kind === "payment" ? "green.500" : "rgb(51, 62, 96)"}
            >
              <Icon as={event.kind === "payment" ? MdPayment : AiOutlineDeploymentUnit} w="15px" h="15px" />
            </Flex>
            <Text fontSize="xs" color="secondaryGray.600">
              {dubaiTime(event.at, "DD MMM YYYY, HH:mm:ss")}
            </Text>
            <Text fontWeight="700" fontSize="sm">
              {event.title}
            </Text>
            {event.detail ? (
              <Text fontSize="sm" color="secondaryGray.700" wordBreak="break-word">
                {event.detail}
              </Text>
            ) : null}
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

export default function AloOrderDrawer({ orderId, preview, isOpen, onClose, onChanged }) {
  const toast = useToast();
  const withFreshToken = useWithFreshToken();
  const bgBox = useColorModeValue("#F4F8FE", "rgb(10, 20, 54)");
  const [order, setOrder] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [smsTo, setSmsTo] = useState("");
  const [emailTo, setEmailTo] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);
  // Set when a voucher retry was queued; the backend issues the code in the background within ~2 minutes.
  const [retryQueuedAt, setRetryQueuedAt] = useState(null);

  const shown = order || preview;

  const applyOrder = useCallback((detail) => {
    setOrder(detail);
    setSmsTo(detail && detail.phone ? detail.phone : "");
    setEmailTo(detail && detail.email ? detail.email : "");
  }, []);

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setLoadError(null);
    try {
      applyOrder(await withFreshToken(() => aloApi.getOrder(orderId)));
    } catch (error) {
      setLoadError(error);
    } finally {
      setLoading(false);
    }
  }, [orderId, withFreshToken, applyOrder]);

  useEffect(() => {
    if (isOpen) {
      setOrder(null);
      setRetryQueuedAt(null);
      setSmsTo(preview && preview.phone ? preview.phone : "");
      setEmailTo(preview && preview.email ? preview.email : "");
      load();
    }
  }, [isOpen, orderId]);

  // After a queued retry, look again once the background job has had time to run.
  useEffect(() => {
    if (!isOpen || !retryQueuedAt) return undefined;
    const timer = setTimeout(() => {
      load();
      if (onChanged) onChanged();
    }, RETRY_RECHECK_MS);
    return () => clearTimeout(timer);
  }, [isOpen, retryQueuedAt]);

  // Once the code shows up, the queued-retry notice has done its job.
  useEffect(() => {
    if (order && order.status === "DELIVERED") setRetryQueuedAt(null);
  }, [order && order.status]);

  const runAction = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      const outcome = await withFreshToken(confirm.run);
      toast({ title: confirm.success(outcome), status: "success", duration: 6000, isClosable: true, position: "top" });
      setConfirm(null);
      if (outcome && outcome.retryQueued) setRetryQueuedAt(new Date());
      if (outcome && outcome.orderId) applyOrder(outcome);
      else await load();
      if (onChanged) onChanged();
    } catch (error) {
      toast({
        title: confirm.failureTitle,
        description: describeError(error),
        status: "error",
        duration: 8000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setBusy(false);
    }
  };

  const isNumber = (value) => value !== null && value !== undefined && value !== "" && !Number.isNaN(Number(value));
  const fee =
    shown && isNumber(shown.amount) && isNumber(shown.faceValue)
      ? Math.round((Number(shown.amount) - Number(shown.faceValue)) * 100) / 100
      : null;
  const paid = Boolean(shown && shown.paidAt);
  const canRetry = order && paid && order.status !== "DELIVERED" && !retryQueuedAt;
  const hasCode = Boolean(order && order.voucher && order.voucher.code);
  const smsChanged = order && smsTo.trim() && smsTo.trim() !== (order.phone || "");
  const emailChanged = order && emailTo.trim() && emailTo.trim() !== (order.email || "");

  const nowrap = (value) => (
    <Text as="span" fontWeight="700" whiteSpace="nowrap">
      {value}
    </Text>
  );

  const askResendSms = () =>
    setConfirm({
      title: "Resend code by SMS",
      body: (
        <>
          Send the alo code for order #{order.orderId} by SMS to {nowrap(displayPhone(smsTo.trim() || order.phone))}?
        </>
      ),
      confirmLabel: "Send SMS",
      failureTitle: "SMS not sent",
      run: () => aloApi.resend(order.orderId, "SMS", smsChanged ? smsTo.trim() : undefined),
      success: (result) => `Code sent by SMS to ${displayPhone(result && result.to ? result.to : smsTo)}`,
    });

  const askResendEmail = () =>
    setConfirm({
      title: "Resend code by email",
      body: (
        <>
          Send the alo code for order #{order.orderId} by email to {nowrap(emailTo.trim() || order.email || "the customer")}?
        </>
      ),
      confirmLabel: "Send email",
      failureTitle: "Email not sent",
      run: () => aloApi.resend(order.orderId, "EMAIL", emailChanged ? emailTo.trim() : undefined),
      success: (result) => `Code emailed to ${result && result.to ? result.to : emailTo}`,
    });

  const askRetry = () =>
    setConfirm({
      title: "Retry the voucher",
      body: `Ask Giftery to issue the code for order #${order.orderId} again. It runs in the background within about 2 minutes, and the customer gets the code by SMS automatically. If the last attempt's result is unknown, check the Giftery transaction first.`,
      confirmLabel: "Retry voucher",
      failureTitle: "Retry not queued",
      run: () => aloApi.retryVoucher(order.orderId),
      success: (detail) =>
        detail && detail.retryQueued
          ? "Retry queued — the code will be issued within about 2 minutes"
          : `Retry done: ${statusLabel(detail && detail.status)}`,
    });

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="lg">
      <DrawerOverlay />
      <DrawerContent background={bgBox}>
        <DrawerCloseButton mt={2} />
        <DrawerHeader>
          <Flex align="center" gap={3} wrap="wrap" pr={8}>
            <Text>Order #{orderId}</Text>
            {shown ? <StatusBadge status={shown.status} fontSize="sm" /> : null}
          </Flex>
          <Text fontSize="sm" fontWeight="500" color="secondaryGray.600" mt={1}>
            {shown ? `${dubaiTime(shown.createdAt, "DD MMM YYYY, HH:mm")} (Dubai)` : " "}
            {order && order.customerLink ? (
              <Link href={order.customerLink} isExternal color="orange.400" ms={3} fontWeight="700">
                Open customer page <Icon as={MdOpenInNew} mb="-2px" />
              </Link>
            ) : null}
          </Text>
        </DrawerHeader>

        <DrawerBody pb={10}>
          {loadError ? (
            <Alert status="error" borderRadius="12px" mb={4}>
              <AlertIcon />
              <AlertDescription flex="1">{describeError(loadError)}</AlertDescription>
              <Button size="sm" leftIcon={<MdRefresh />} onClick={load}>
                Retry
              </Button>
            </Alert>
          ) : null}

          <Stack spacing={5}>
            <Card p="20px">
              <Heading size="sm" mb={4}>
                Customer
              </Heading>
              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
                <Field label="Phone">
                  {shown && shown.phone ? (
                    <Link href={telHref(shown.phone)} color="orange.400">
                      <Icon as={MdCall} mb="-2px" me={1} />
                      {displayPhone(shown.phone)}
                    </Link>
                  ) : (
                    "—"
                  )}
                </Field>
                <Field label="Email">
                  {shown && shown.email ? (
                    <Link href={`mailto:${shown.email}`} color="orange.400">
                      {shown.email}
                    </Link>
                  ) : (
                    "Not given"
                  )}
                </Field>
              </Grid>
            </Card>

            <Card p="20px">
              <Heading size="sm" mb={4}>
                Payment
              </Heading>
              <Skeleton isLoaded={Boolean(shown)} borderRadius={12}>
                <Grid templateColumns={{ base: "1fr 1fr", md: "repeat(3, 1fr)" }} gap={4}>
                  <Field label="Bundle">{shown ? shown.productName : "—"}</Field>
                  <Field label="Bundle value">{shown ? money(shown.faceValue, shown.currency) : "—"}</Field>
                  <Field label="Card fee">{fee !== null ? money(fee, shown.currency) : "—"}</Field>
                  <Field label="Amount paid">{shown ? money(shown.amount, shown.currency) : "—"}</Field>
                  <Field label="Paid at">{shown && shown.paidAt ? dubaiTime(shown.paidAt) : "Not paid"}</Field>
                  <Field label="PayBy order">
                    <CopyValue value={shown && shown.paymentOrderNo} />
                  </Field>
                  <Field label="Zoho sales order">
                    <CopyValue value={order && order.salesOrderNo} />
                  </Field>
                  <Field label="Giftery transaction">
                    <CopyValue value={order && order.voucher && order.voucher.transactionUuid} />
                  </Field>
                  <Field label="Customer ID">{order && order.userId ? order.userId : "—"}</Field>
                </Grid>
              </Skeleton>
            </Card>

            <Card p="20px">
              <Heading size="sm" mb={4}>
                alo code
              </Heading>
              <Skeleton isLoaded={!loading || Boolean(order)} borderRadius={12}>
                <VoucherCode voucher={order && order.voucher} />
              </Skeleton>
            </Card>

            <Card p="20px">
              <Heading size="sm" mb={1}>
                Actions
              </Heading>
              <Text fontSize="sm" color="secondaryGray.600" mb={4}>
                Every action asks for confirmation and is recorded in the order history.
              </Text>
              <Stack spacing={4}>
                <Box>
                  <Text fontSize="sm" fontWeight="700" mb={2}>
                    Resend code by SMS
                  </Text>
                  <Flex gap={2} direction={{ base: "column", md: "row" }}>
                    <InputGroup>
                      <Input
                        value={smsTo}
                        onChange={(e) => setSmsTo(e.target.value)}
                        placeholder="971501234567"
                        inputMode="tel"
                        isDisabled={!order}
                      />
                      {smsChanged ? (
                        <InputRightElement width="auto" pe={3}>
                          <Text fontSize="xs" color="orange.500" fontWeight="700">
                            new number
                          </Text>
                        </InputRightElement>
                      ) : null}
                    </InputGroup>
                    <Tooltip label={hasCode ? "" : "No code issued yet"} isDisabled={hasCode}>
                      <Button leftIcon={<MdSms />} colorScheme="orange" onClick={askResendSms} isDisabled={!hasCode} flexShrink={0}>
                        Resend SMS
                      </Button>
                    </Tooltip>
                  </Flex>
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="700" mb={2}>
                    Resend code by email
                  </Text>
                  <Flex gap={2} direction={{ base: "column", md: "row" }}>
                    <Input
                      value={emailTo}
                      onChange={(e) => setEmailTo(e.target.value)}
                      placeholder="customer@example.com"
                      type="email"
                      isDisabled={!order}
                    />
                    <Tooltip label={hasCode ? "" : "No code issued yet"} isDisabled={hasCode}>
                      <Button
                        leftIcon={<MdEmail />}
                        colorScheme="orange"
                        variant="outline"
                        onClick={askResendEmail}
                        isDisabled={!hasCode || !emailTo.trim()}
                        flexShrink={0}
                      >
                        Resend email
                      </Button>
                    </Tooltip>
                  </Flex>
                </Box>
                {canRetry || retryQueuedAt ? <Divider /> : null}
                {retryQueuedAt ? (
                  <Alert status="info" borderRadius="12px">
                    <AlertIcon />
                    <AlertDescription flex="1" fontSize="sm">
                      Retry queued at {dubaiTime(retryQueuedAt.toISOString(), "HH:mm")}. The code will be issued within
                      about 2 minutes; this order is checked again automatically.
                    </AlertDescription>
                    <Button size="sm" leftIcon={<MdRefresh />} onClick={load} isLoading={loading} ms={2}>
                      Refresh
                    </Button>
                  </Alert>
                ) : null}
                {canRetry ? (
                  <Flex gap={3} wrap="wrap">
                    <Button leftIcon={<MdReplay />} colorScheme="red" variant="outline" onClick={askRetry}>
                      Retry voucher
                    </Button>
                  </Flex>
                ) : null}
              </Stack>
            </Card>

            <Card p="20px">
              <Flex justify="space-between" align="center" mb={2}>
                <Heading size="sm">History</Heading>
                <Button size="xs" variant="ghost" leftIcon={<MdRefresh />} onClick={load} isLoading={loading}>
                  Refresh
                </Button>
              </Flex>
              <Skeleton isLoaded={Boolean(order)} borderRadius={12} minH="60px">
                {order ? <Timeline order={order} /> : null}
              </Skeleton>
            </Card>
          </Stack>
        </DrawerBody>
      </DrawerContent>

      <ConfirmDialog request={confirm} isBusy={busy} onCancel={() => setConfirm(null)} onConfirm={runAction} />
    </Drawer>
  );
}
